import { css } from '@emotion/react'
import DeleteIcon from '@mui/icons-material/Delete'

import ModalHeader from '../ModalHeader'
import ModalBody from '../ModalBody'
import ModalFooter from '../ModalFooter'
import { Modal, ModalSize } from '../Modal/Modal'
import { PrimaryButton, SecondaryButton } from '../../buttons'
import useModal from '../../../../lib/hooks/useModal'
import { errorTextColor } from '../../../../lib/styles/colors'

export type ConfirmModalProps = {
  /** Text of Cancel Button */
  cancelText?: string
  /** Body content */
  children: React.ReactNode
  /** Flag for whether the confirm button should be disabled */
  confirmButtonDisabled?: boolean
  /** Text of Confirm Button */
  confirmText?: string
  /** Indicates the modal should be in DANGER mode with danger border and button */
  isDanger?: boolean
  /** Determines visibility of modal */
  isOpen: boolean
  /** Event callback when Cancel Button is triggered */
  onClose: () => void
  /** Event callback when Confirm Button is triggered */
  onConfirm: () => void
  /** Size of modal */
  size?: ModalSize
  /** Title text of modal */
  title: string
}

const bodyCss = css`
  padding-bottom: 40px;
  margin-top: 20px;
`
const confirmCss = css`
  margin-left: 12px;
`
const dangerTitleCss = css`
  display: inline-flex;
  align-items: center;

  svg {
    padding-right: 8px;
    color: ${errorTextColor};
  }
`

export const ConfirmModal = ({
  cancelText = 'Cancel',
  children,
  confirmButtonDisabled = false,
  confirmText = 'Confirm',
  isDanger = false,
  isOpen,
  onClose,
  onConfirm,
  size = ModalSize.SM,
  title
}: ConfirmModalProps) => {
  // A11Y-537: Use descriptive aria-labels for buttons (WCAG 2.4.6 Level AA)
  // Adds context so screen reader users navigating button lists know what action confirms
  const confirmButton = isDanger ? (
    <PrimaryButton
      ariaLabel={`Confirm: ${confirmText}`}
      css={confirmCss}
      color={errorTextColor}
      disabled={confirmButtonDisabled}
      onClick={onConfirm}
    >
      {confirmText}
    </PrimaryButton>
  ) : (
    <SecondaryButton
      ariaLabel={`Confirm: ${confirmText}`}
      css={confirmCss}
      disabled={confirmButtonDisabled}
      onClick={onConfirm}
    >
      {confirmText}
    </SecondaryButton>
  )
  const headerTitle = isDanger ? (
    <span css={dangerTitleCss}>
      <DeleteIcon /> {title}
    </span>
  ) : (
    title
  )

  return (
    // A11Y-536: Pass onClose to enable aria-labelledby for dialog title (WCAG 1.3.1, 4.1.2 Level A)
    <Modal isDanger={isDanger} isOpen={isOpen} onClose={onClose} size={size}>
      <ModalHeader onClose={isDanger ? onClose : undefined} title={headerTitle} />
      <ModalBody css={bodyCss}>{children}</ModalBody>
      <ModalFooter>
        {/* A11Y-537: Descriptive aria-label provides context for screen reader users (WCAG 2.4.6 Level AA) */}
        <SecondaryButton ariaLabel="Cancel plan deletion" onClick={onClose}>
          {cancelText}
        </SecondaryButton>
        {confirmButton}
      </ModalFooter>
    </Modal>
  )
}

type ConfirmModalApolloProps = {
  cancelText?: string
  children: React.ReactNode
  confirmButtonDisabled?: boolean
  confirmText?: string
  isDanger?: boolean
  isOpen: boolean
  onConfirm: () => void
  size?: ModalSize
  title: string
}

const ConfirmModalWrapper = ({
  cancelText = 'Cancel',
  children,
  confirmButtonDisabled = false,
  confirmText = 'Confirm',
  isDanger = false,
  isOpen,
  onConfirm,
  size = ModalSize.SM,
  title
}: ConfirmModalApolloProps) => {
  const { hideModal } = useModal()
  const onClose = () => hideModal()

  return (
    <ConfirmModal
      cancelText={cancelText}
      confirmButtonDisabled={confirmButtonDisabled}
      confirmText={confirmText}
      isDanger={isDanger}
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      size={size}
      title={title}
    >
      {children}
    </ConfirmModal>
  )
}

export default ConfirmModalWrapper
